import React, { useState, useEffect } from 'react'
import { submitUpgradeRequest, getCommunity } from '../services/onboardingService'

/**
 * TrialGate — wraps features that may be restricted based on the community's
 * FEATURE_FLAGS. During the trial period children are rendered normally.
 * When a flag is explicitly false/exceeded a placeholder is shown instead.
 *
 * Props:
 *   feature      {string}       — key inside FEATURE_FLAGS (e.g. "max_residents")
 *   featureFlags {object}       — the FEATURE_FLAGS object from the community record
 *   currentCount {number}       — (optional) current usage count for numeric limits
 *   label        {string}       — human-readable feature name shown in the locked view
 *   children     {React.node}   — content to render when the feature is accessible
 *
 * Locking logic:
 *   - If featureFlags is null/undefined → always render children (no data yet)
 *   - If featureFlags[feature] === false → locked
 *   - If featureFlags[feature] is a number and currentCount >= that number → locked
 *   - Otherwise → render children
 *
 * Role-based locked view:
 *   - resident / non-resident → "Contact Your Community Administrator For Plan Upgrade"
 *   - admin → clickable "Upgrade to unlock" button that submits a request; shows confirmation on success
 *   - other roles → generic badge
 */
export default function TrialGate({ feature, featureFlags, currentCount, label, children }) {
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  const [flags, setFlags] = useState(featureFlags)

  useEffect(() => {
    setFlags(featureFlags)
  }, [featureFlags])

  const refreshFlags = async () => {
    const communityId = sessionStorage.getItem('community_id') || ''
    if (!communityId) return
    setRefreshing(true)
    try {
      const comm = await getCommunity({ communityId })
      if (comm?.FEATURE_FLAGS) setFlags(comm.FEATURE_FLAGS)
    } catch (err) {
      // ignore transient failures
    } finally {
      setRefreshing(false)
    }
  }

  useEffect(() => {
    if (!feature || !flags) return
    let mounted = true
    const communityId = sessionStorage.getItem('community_id') || ''

    const refreshOnBackground = async () => {
      if (!communityId) return
      try {
        const comm = await getCommunity({ communityId })
        if (!mounted) return
        if (comm?.FEATURE_FLAGS) setFlags(comm.FEATURE_FLAGS)
      } catch (err) {
        // ignore transient failures
      }
    }

    window.addEventListener('focus', refreshOnBackground)
    const intervalId = window.setInterval(refreshOnBackground, 15000)
    return () => {
      mounted = false
      window.removeEventListener('focus', refreshOnBackground)
      window.clearInterval(intervalId)
    }
  }, [feature, flags])

  if (!flags || feature === undefined) {
    return <>{children}</>
  }

  const flag = flags[feature]

  let locked = false
  if (flag === false) {
    locked = true
  } else if (typeof flag === 'number' && typeof currentCount === 'number' && currentCount >= flag) {
    locked = true
  }

  if (!locked) {
    return <>{children}</>
  }

  const userRole = (sessionStorage.getItem('user_role') || '').toLowerCase()
  const isResident = userRole === 'resident' || userRole === 'non-resident'
  const isAdmin = userRole === 'admin'

  const description = typeof flag === 'number'
    ? `You've reached the ${label || feature} limit of ${flag} on your current plan.`
    : `${label || feature} is not available on your current plan.`

  async function handleUpgradeRequest() {
    setSubmitting(true)
    setSubmitError('')
    try {
      const communityId = sessionStorage.getItem('community_id') || ''
      const communityName = sessionStorage.getItem('community_name') || ''
      const idToken = sessionStorage.getItem('id_token') || ''
      await submitUpgradeRequest({ communityId, communityName, feature, label: label || feature }, idToken)
      setSubmitted(true)
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit request. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={styles.wrapper}>
      <div style={styles.overlay}>
        <div style={styles.icon}>🔒</div>
        <h3 style={styles.title}>{label || feature} Not Available</h3>
        <p style={styles.desc}>{description}</p>

        {isResident && (
          <div style={styles.residentBadge}>
            Contact Your Community Administrator For Plan Upgrade
          </div>
        )}

        {isAdmin && !submitted && (
          <>
            {submitError && <p style={styles.errorText}>{submitError}</p>}
            <button
              style={styles.upgradeBtn}
              onClick={handleUpgradeRequest}
              disabled={submitting}
            >
              {submitting ? 'Submitting…' : 'Upgrade to unlock'}
            </button>
          </>
        )}

        {refreshing && (
          <span style={{ fontSize: 12, color: '#6b7280' }}>Refreshing…</span>
        )}

        {!refreshing && (
          <button
            onClick={refreshFlags}
            disabled={refreshing}
            style={{
              background: '#e5e7eb',
              color: '#111827',
              border: 'none',
              borderRadius: 6,
              padding: '6px 14px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              marginTop: 8,
            }}
          >
            Refresh
          </button>
        )}

        {isAdmin && submitted && (
          <div style={styles.successBox}>
            <div style={{ fontSize: 22, marginBottom: 6 }}>✅</div>
            <p style={styles.successText}>
              Upgrade request submitted! A sales representative will contact you to discuss rates, payment method, and approval.
            </p>
          </div>
        )}

        {!isResident && !isAdmin && (
          <div style={styles.badge}>Upgrade to unlock</div>
        )}
      </div>
      {/* Render blurred children as visual background */}
      <div style={{ opacity: 0.15, pointerEvents: 'none', userSelect: 'none' }}>
        {children}
      </div>
    </div>
  )
}

const styles = {
  wrapper: {
    position: 'relative',
    borderRadius: 12,
    overflow: 'hidden',
    border: '2px dashed #d1d5db',
    minHeight: 200,
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.92)',
    zIndex: 10,
    padding: 24,
    gap: 8,
    textAlign: 'center',
  },
  icon: { fontSize: 36 },
  title: { fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 },
  desc: { fontSize: 13, color: '#6b7280', maxWidth: 320, lineHeight: 1.5, margin: 0 },
  badge: {
    background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
    borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 600, marginTop: 4,
  },
  residentBadge: {
    background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0',
    borderRadius: 20, padding: '6px 16px', fontSize: 12, fontWeight: 600, marginTop: 4,
    maxWidth: 320, lineHeight: 1.5,
  },
  upgradeBtn: {
    background: '#2563eb', color: '#fff', border: 'none',
    borderRadius: 20, padding: '8px 20px', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', marginTop: 4,
  },
  successBox: {
    background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10,
    padding: '12px 20px', maxWidth: 340, marginTop: 4,
  },
  successText: {
    fontSize: 13, color: '#15803d', margin: 0, lineHeight: 1.5,
  },
  errorText: {
    fontSize: 12, color: '#dc2626', margin: '0 0 4px',
  },
}
